import { useCallback, useEffect, useId, useRef, useState } from "react";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import TextInput from "components/TextInput";
import PageTemplate from "modules/common/PageTemplate";

import { AssetsApi, PortfoliosApi } from "services/http";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import { AssetItem, CreateAssetRequest, UpdateAssetRequest } from "entities/assets";
import { AssetFormValues, BaseOption, ModalType } from "entities/common";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  createAssetRequest,
  resetSuccessfullyAddedAsset,
  resetSuccessfullyUpdatedAsset,
  updateAssetRequest,
} from "store/slices/assetSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";

import styles from "./style.module.scss";

const mapAssetToForm = (asset: AssetItem): AssetFormValues => ({
  name: asset.name || "",
  type: asset.type || "",
  description: asset.description || "",
  number: "",
  managerId: asset.manager_id ? String(asset.manager_id) : "",
  images: (asset.images || []).map((img) => ({ id: String(img.id), url: img.url })),
});

const AssetsPage = () => {
  const dispatch = useAppDispatch();
  const callbackKey = useId();
  const { successfullyAddedId, successfullyUpdatedId } = useAppSelector((state: RootStore) => state.assets);

  const [portfolio, setPortfolio] = useState<BaseOption | null>(null);
  const [assetId, setAssetId] = useState("");
  const portfolioCompanies = useRef<Record<string, number>>({});

  const handlePortfolioSearch = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await PortfoliosApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: 50,
        }),
      );

      (data?.items || []).forEach((item) => {
        portfolioCompanies.current[String(item.id)] = item.company_id;
      });

      const options = (data?.items || []).map((item) => ({ value: String(item.id), label: item.name }));
      const hasMore = data?.total ? page * 50 < data.total : false;

      return { options, hasMore };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const handleCreate = useCallback(() => {
    if (!portfolio) {
      dispatch(
        showToast({
          id: "asset-create-no-portfolio",
          message: "Select a portfolio first.",
          type: "error",
          duration: 3000,
        }),
      );
      return;
    }

    const onSave = (values?: unknown) => {
      const data = values as AssetFormValues;

      const body: CreateAssetRequest = {
        name: data.name,
        type: data.type || null,
        description: data.description || null,
        portfolio_id: Number(portfolio.value),
        manager_id: Number(data.managerId),
      };

      dispatch(createAssetRequest({ body, images: data.images }));
    };

    setModalCallbacks(callbackKey, { onSave });
    dispatch(
      showModal({
        modalType: ModalType.CREATE_ASSET,
        modalProps: { mode: "create", callbackKey, companyId: portfolioCompanies.current[portfolio.value] },
      }),
    );
  }, [dispatch, callbackKey, portfolio]);

  const handleEdit = useCallback(async () => {
    if (!assetId) {
      dispatch(
        showToast({ id: "asset-edit-no-id", message: "Enter an asset ID.", type: "error", duration: 3000 }),
      );
      return;
    }

    let asset: AssetItem;
    try {
      const { data } = await AssetsApi.getCurrent(assetId);
      asset = data;
    } catch {
      dispatch(
        showToast({ id: "asset-edit-load-failed", message: "Failed to load asset.", type: "error", duration: 3000 }),
      );
      return;
    }

    const onSave = (values?: unknown) => {
      const data = values as AssetFormValues;

      const body: UpdateAssetRequest = {
        name: data.name,
        type: data.type || null,
        description: data.description || null,
        portfolio_id: asset.portfolio_id,
        manager_id: Number(data.managerId),
      };

      const remainingExistingIds = new Set(
        data.images
          .filter((img) => !img.file && img.id !== undefined && img.id !== null)
          .map((img) => Number(img.id))
          .filter((n) => !Number.isNaN(n)),
      );
      const removedImageIds = (asset.images || [])
        .map((img) => img.id)
        .filter((existingId) => !remainingExistingIds.has(existingId));
      const newImages = data.images.filter((img) => Boolean(img.file));

      dispatch(updateAssetRequest({ id: asset.id, body, newImages, removedImageIds }));
    };

    setModalCallbacks(callbackKey, { onSave });
    dispatch(
      showModal({
        modalType: ModalType.CREATE_ASSET,
        modalProps: {
          mode: "edit",
          id: asset.id,
          callbackKey,
          companyId: asset.company_id ?? undefined,
          initialData: mapAssetToForm(asset),
        },
      }),
    );
  }, [dispatch, callbackKey, assetId]);

  const handleRemove = useCallback(async () => {
    if (!assetId) {
      dispatch(
        showToast({ id: "asset-delete-no-id", message: "Enter an asset ID.", type: "error", duration: 3000 }),
      );
      return;
    }

    let asset: AssetItem;
    try {
      const { data } = await AssetsApi.getCurrent(assetId);
      asset = data;
    } catch {
      dispatch(
        showToast({ id: "asset-delete-load-failed", message: "Failed to load asset.", type: "error", duration: 3000 }),
      );
      return;
    }

    dispatch(showModal({ modalType: ModalType.REMOVE_ASSET, modalProps: { id: asset.id, name: asset.name } }));
  }, [dispatch, assetId]);

  useEffect(() => {
    if (successfullyAddedId) {
      dispatch(hideModal());
      dispatch(resetSuccessfullyAddedAsset());
      dispatch(
        showToast({
          id: "successfully-added-asset",
          message: "The asset was successfully created.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyAddedId, dispatch]);

  useEffect(() => {
    if (successfullyUpdatedId) {
      dispatch(hideModal());
      dispatch(resetSuccessfullyUpdatedAsset());
      dispatch(
        showToast({
          id: "successfully-updated-asset",
          message: "The asset was successfully updated.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyUpdatedId, dispatch]);

  return (
    <PageTemplate title="Assets" description="Trigger asset modals" className={styles.page}>
      <div className={styles.wrapper}>
        <div className={styles.controls}>
        <AsyncSearchableSelect
          label="Portfolio"
          placeholder="Select portfolio"
          value={portfolio?.value}
          selectedLabel={portfolio?.label}
          onChange={(value, label) => setPortfolio({ value, label })}
          onSearch={handlePortfolioSearch}
          className={styles.control}
        />

        <TextInput
          label="Asset ID"
          placeholder="Asset ID"
          value={assetId}
          onChange={(e) => setAssetId(e.target.value.replace(/\D/g, ""))}
          className={styles.control}
        />
      </div>

      <div className={styles.actions}>
        <Button variant="primary" onClick={handleCreate}>
          Create asset
        </Button>

        <Button variant="secondary" onClick={handleEdit}>
          Edit asset
        </Button>

          <Button variant="secondary" onClick={handleRemove}>
            Delete asset
          </Button>
        </div>
      </div>
    </PageTemplate>
  );
};

export default AssetsPage;
